import type { Member } from '@stytch/nextjs/b2b'
import type { IdentityInfoSchemaType, OrgSchemaType } from '@/types/portal-api.generated'
import {
  emitSelectedClientIdChanged,
  setSelectedClientId,
} from '@/utilities/client/selectedClientIdStorage'
import { PayloadRepository } from '@/repositories/payloadRepository'
import { syncPostHogPartnerGroup } from '@/utilities/analytics/partnerGroupAnalytics'

export type PostAuthDependencies = {
  loginUser: (member: Member) => void
  setOrganization: (organization: OrgSchemaType) => void
  identifyUser: (member: Member | undefined) => void
  identifyGroup: (organization: OrgSchemaType) => void
  syncUserToPayload: (member: Member, orgSlug: string) => Promise<unknown>
}

export type SelectedClientInitializationResult =
  | { kind: 'initialized'; selectedClientId: string }
  | { kind: 'rejected'; reason: 'email_mismatch' }
  | {
      kind: 'unavailable'
      reason: 'missing_scopes' | 'client_user_failed'
    }

type IdentityScopes = IdentityInfoSchemaType['scopes']
type NarrowedIdentityScope = { roles?: unknown; is_primary?: unknown } // IdentityScopes[string] is too broad

type ClientUserCredentials = {
  email?: string
  first_name?: string
  last_name?: string
  user_id?: string
  is_active?: boolean
  partner_id?: string
}

type ClientScopeEntry = {
  rawKey: string
  normalizedKey: string
  scope: NarrowedIdentityScope
}

type WrappedClientUserResponse = {
  error?: unknown
  data?: ClientUserCredentials | null
}

type BaseSelectedClientInitializationArgs = {
  scopes: IdentityScopes
  getClientUser: (args: { clientId: string }) => Promise<unknown>
  identityEmail?: string
  organizationId?: string
  preferredClientId?: string | null
}

type StrictSelectedClientInitializationArgs = BaseSelectedClientInitializationArgs & {
  initializationMode?: 'strict_member_match'
}

type VerifiedAuthUserSelectedClientInitializationArgs = BaseSelectedClientInitializationArgs & {
  initializationMode: 'verified_auth_user'
  verifiedAuthUserId: string | number
}

type SelectedClientInitializationArgs =
  StrictSelectedClientInitializationArgs | VerifiedAuthUserSelectedClientInitializationArgs

const isWrappedClientUserResponse = (value: unknown): value is WrappedClientUserResponse =>
  typeof value === 'object' && value !== null && ('data' in value || 'error' in value)

const extractClientUserCredentials = (value: unknown): ClientUserCredentials | null => {
  if (isWrappedClientUserResponse(value)) {
    return value.data ?? null
  }

  if (typeof value !== 'object' || value === null) {
    return null
  }

  return value as ClientUserCredentials
}

/**
 * Handles the post-authentication logic for the member.
 *
 * @param member - The member object.
 * @param orgSlug - The organization slug.
 * @param deps - The dependencies.
 */
export async function handlePostAuth(
  member: Member,
  orgSlug: string,
  deps: PostAuthDependencies,
): Promise<void> {
  const { syncUserToPayload } = deps
  applyPostAuthBase(member, orgSlug, deps)
  if (orgSlug === 'client') {
    await syncUserToPayload(member, orgSlug)
  }
}

/**
 * Applies the common post-authentication side-effects (analytics, org state) without
 * performing any client-portal specific synchronization.
 */
export function applyPostAuthBase(
  member: Member,
  orgSlug: string,
  deps: PostAuthDependencies,
): void {
  const { loginUser, setOrganization, identifyUser, identifyGroup } = deps

  if (member.organization_id) {
    const organization: OrgSchemaType = {
      id: member.organization_id,
      name: '',
      slug: orgSlug,
      organizationId: member.organization_id,
      primaryProgramId: '',
      roles: [],
      scopes: {},
    }

    loginUser(member)
    setOrganization(organization)
    identifyUser(member)
    // Client Portal uses partner ID for the `client` group after client selection — not the Client Portal org.
    if (orgSlug !== 'client') {
      identifyGroup(organization)
    }
  } else {
    loginUser(member)
    identifyUser(member)
  }
}

export const normalizeClientId = (clientId: string) =>
  clientId.toLowerCase() === 'ash' ? 'demopartner' : clientId

export const normalizeNullableClientId = (clientId: string | null | undefined): string | null => {
  const trimmed = clientId?.trim() || null
  return trimmed ? normalizeClientId(trimmed) : null
}

/** Scope keys that are not selectable partner IDs (Ash admin wildcards / portal role buckets). */
export const isSelectableScopeKey = (scopeKey: string) =>
  scopeKey !== '*' && scopeKey !== 'client-portal'

const narrowIdentityScope = (scope: IdentityScopes[string] | undefined): NarrowedIdentityScope => {
  if (typeof scope !== 'object' || scope == null) {
    return {}
  }

  return scope as NarrowedIdentityScope
}

const getClientScopeEntries = (scopes: IdentityScopes): ClientScopeEntry[] =>
  Object.entries(scopes || {}).map(([key, value]) => ({
    rawKey: key,
    normalizedKey: normalizeClientId(key),
    scope: narrowIdentityScope(value),
  }))

const resolveSelectedClientIdFromEntries = ({
  entries,
  preferredClientId,
}: {
  entries: ClientScopeEntry[]
  preferredClientId?: string | null
}): string | null => {
  const selectableEntries = entries.filter(({ rawKey }) => isSelectableScopeKey(rawKey))
  const selectableEntriesWithRoles = selectableEntries.filter(
    ({ scope }) => Array.isArray(scope.roles) && scope.roles.length > 0,
  )
  const normalizedPreferredClientId =
    preferredClientId && isSelectableScopeKey(preferredClientId)
      ? normalizeClientId(preferredClientId)
      : null
  const preferredEntry =
    normalizedPreferredClientId == null
      ? null
      : selectableEntries.find(({ normalizedKey }) => normalizedKey === normalizedPreferredClientId)
  const primaryRoleEntry =
    selectableEntriesWithRoles.find(({ scope }) => scope.is_primary === true) ?? null
  const primaryEntry = selectableEntries.find(({ scope }) => scope.is_primary === true) ?? null
  const initial =
    preferredEntry ?? primaryRoleEntry ?? primaryEntry ?? selectableEntriesWithRoles[0] ?? null

  return initial ? initial.normalizedKey : null
}

/**
 * Pick an authorized selectable partner id from identity scopes.
 * Prefers `preferredClientId` when it matches a selectable scope key; otherwise
 * primary-with-roles, then any primary, then the first selectable entry with roles.
 * Returns normalized ids (`ash` → `demopartner`). Sentinel keys are never selected.
 */
export function resolveAuthorizedPartnerId(
  scopes: IdentityScopes,
  preferredClientId?: string | null,
): string | null {
  return resolveSelectedClientIdFromEntries({
    entries: getClientScopeEntries(scopes),
    preferredClientId,
  })
}

async function hydrateClientUserForSelectedClient(args: {
  getClientUser: (args: { clientId: string }) => Promise<unknown>
  selectedClientId: string
  identityEmail?: string
}): Promise<
  | { kind: 'matched'; credentials: ClientUserCredentials }
  | { kind: 'mismatch' }
  | { kind: 'unavailable' }
> {
  const { getClientUser, selectedClientId, identityEmail } = args

  try {
    const clientUserResp = await getClientUser({
      clientId: selectedClientId,
    })
    const wrappedResponse = isWrappedClientUserResponse(clientUserResp) ? clientUserResp : null

    if (wrappedResponse?.error) {
      return { kind: 'unavailable' }
    }

    const credentials = extractClientUserCredentials(clientUserResp)
    if (!credentials) {
      return { kind: 'unavailable' }
    }

    const normalizedIdentityEmail = identityEmail?.trim().toLowerCase()
    const normalizedCredentialEmail = credentials.email?.trim().toLowerCase()

    if (
      normalizedIdentityEmail &&
      normalizedCredentialEmail &&
      normalizedCredentialEmail !== normalizedIdentityEmail
    ) {
      return { kind: 'mismatch' }
    }

    return { kind: 'matched', credentials }
  } catch {
    return { kind: 'unavailable' }
  }
}

/**
 * Initializes the selected client from identity scopes, optionally hydrates client user info,
 * then persists to storage and emits a change event. Partner data is loaded once by the
 * Client Portal context after navigation.
 * Returns the selected client ID when successful.
 */
export async function initializeSelectedClientFromIdentity(
  args: SelectedClientInitializationArgs,
): Promise<SelectedClientInitializationResult> {
  const {
    scopes,
    getClientUser,
    identityEmail,
    preferredClientId,
    initializationMode = 'strict_member_match',
  } = args
  const selectedClientId = resolveAuthorizedPartnerId(scopes, preferredClientId)
  if (!selectedClientId) {
    return { kind: 'unavailable', reason: 'missing_scopes' }
  }
  const clientUserHydration = await hydrateClientUserForSelectedClient({
    getClientUser,
    selectedClientId,
    identityEmail,
  })

  if (initializationMode === 'strict_member_match') {
    if (clientUserHydration.kind === 'mismatch') {
      return { kind: 'rejected', reason: 'email_mismatch' }
    }
    if (clientUserHydration.kind === 'unavailable') {
      return { kind: 'unavailable', reason: 'client_user_failed' }
    }
  }

  try {
    setSelectedClientId(selectedClientId)
    emitSelectedClientIdChanged(selectedClientId)
    syncPostHogPartnerGroup(selectedClientId)
  } catch {
    /** noop */
  }

  return { kind: 'initialized', selectedClientId }
}

export async function syncPayloadPostClientSelection(args: {
  member: Member
  scopes: IdentityScopes
  selectedClientId?: string
}): Promise<void> {
  const { member, scopes, selectedClientId } = args
  try {
    await PayloadRepository.syncUserToPayload({
      email: member.email_address,
      name: member.name,
      partnerId: selectedClientId,
      scopes,
    })
  } catch {
    /** ignore secondary sync failures */
  }
}
